import { memo, useId } from 'react';
import type { CSSProperties } from 'react';
import { Button } from '@/components/ui/Button';
import { interpolate, useFormatters } from '@/lib/i18n';
import type { VisualizerCopy } from './content';
import { SCALE_MAX, SCALE_MIN, SCALE_STEP } from './url-state';
import type { Rotation } from './url-state';
import { useRovingRadio } from './useRovingRadio';

export interface AdjustmentsProps {
  scale: number;
  /** `commit` is true when the change is final (pointer released, key released, focus left). */
  onScale: (value: number, commit: boolean) => void;
  rotate: Rotation;
  onRotate: (value: Rotation) => void;
  seams: boolean;
  /** Seams only make sense on floors and walls. */
  seamsAvailable: boolean;
  onSeams: (value: boolean) => void;
  /** Anything differs from the defaults (enables Reset). */
  dirty: boolean;
  onReset: () => void;
  copy: VisualizerCopy['adjust'];
}

const ROTATIONS: readonly Rotation[] = [0, 90];

/**
 * The pattern controls: a scale slider (50 – 200 %), the 0° / 90° direction and, on floors and walls,
 * a tile-seams switch. All real form controls — a native range, a radio group, a switch button.
 */
export const Adjustments = memo(function Adjustments({
  scale,
  onScale,
  rotate,
  onRotate,
  seams,
  seamsAvailable,
  onSeams,
  dirty,
  onReset,
  copy,
}: AdjustmentsProps) {
  const { formatNumber } = useFormatters();
  const scaleId = useId();
  const rotateId = useId();
  const percent = Math.round(scale * 100);
  const valueText = interpolate(copy.scaleValue, { value: formatNumber(percent) });

  const item = useRovingRadio<HTMLButtonElement>({
    count: ROTATIONS.length,
    selected: ROTATIONS.indexOf(rotate),
    onSelect: (index) => {
      const next = ROTATIONS[index];
      if (next !== undefined) onRotate(next);
    },
  });

  const range = Math.max(0, Math.min(100, ((scale - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));

  return (
    <div className="viz-adjust">
      <div className="viz-field">
        <div className="viz-field__head">
          <label className="label viz-field__label" htmlFor={scaleId}>
            {copy.scale}
          </label>
          <output className="viz-field__value" htmlFor={scaleId} data-tabular>
            {valueText}
          </output>
        </div>
        <input
          id={scaleId}
          className="viz-range"
          type="range"
          min={SCALE_MIN}
          max={SCALE_MAX}
          step={SCALE_STEP}
          value={scale}
          aria-valuetext={valueText}
          style={{ '--fill': `${range.toFixed(1)}%` } as CSSProperties}
          onChange={(event) => {
            onScale(Number(event.target.value), false);
          }}
          onPointerUp={(event) => {
            onScale(Number(event.currentTarget.value), true);
          }}
          onBlur={(event) => {
            onScale(Number(event.currentTarget.value), true);
          }}
        />
        <div className="viz-range__ends" aria-hidden="true">
          <span>{formatNumber(Math.round(SCALE_MIN * 100))} %</span>
          <span>{formatNumber(Math.round(SCALE_MAX * 100))} %</span>
        </div>
      </div>

      <div className="viz-field">
        <span className="label viz-field__label" id={rotateId}>
          {copy.rotate}
        </span>
        <div className="viz-segment" role="radiogroup" aria-labelledby={rotateId}>
          {ROTATIONS.map((value, index) => (
            <button key={value} type="button" className="viz-segment__option" {...item(index)}>
              {value === 0 ? copy.rotateOptions.r0 : copy.rotateOptions.r90}
            </button>
          ))}
        </div>
      </div>

      {seamsAvailable ? (
        <button
          type="button"
          role="switch"
          className="viz-switch"
          aria-checked={seams}
          onClick={() => {
            onSeams(!seams);
          }}
        >
          <span className="viz-switch__label">{copy.seams}</span>
          <span className="viz-switch__track" aria-hidden="true">
            <span className="viz-switch__thumb" />
          </span>
        </button>
      ) : null}

      <Button variant="ghost" size="sm" disabled={!dirty} onClick={onReset}>
        {copy.reset}
      </Button>
    </div>
  );
});
